import { Lock, Unlock } from "lucide-react";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Kpi, Section, Spinner } from "@/components/kit";
import { api, brl, brlShort, errMsg } from "@/lib/api";
import { AlertList, can, useBiz, useBizGet, V } from "./shared";
import { TaxReport } from "./TaxReport";

export const TT = { contentStyle: { background: "#0B1A33", border: "1px solid #1E3A6E" }, formatter: (v) => brl(v) };

export function Overview() {
  const { cid, month, role, goTab } = useBiz();
  const { data: a, reload } = useBizGet(`/analysis?month=${month}`);
  if (!a) return <Spinner />;
  const I = a.indicators;
  const H = a.history.at(-1);
  const close = async () => {
    try { await api.post(`/biz/companies/${cid}/months/${month}/close`); toast.success(`Mês ${month} fechado`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const reopen = async () => {
    const reason = window.prompt("Motivo da reabertura (fica registrado na auditoria):");
    if (!reason) { return; }
    try { await api.post(`/biz/companies/${cid}/months/${month}/reopen`, { reason }); toast.success("Mês reaberto"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6" data-testid="biz-overview">
      <div className="flex flex-wrap items-center gap-3">
        <span className={`rounded-full px-3 py-1 text-xs ${a.closed ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-500/15 text-slate-300"}`} data-testid="biz-month-status">
          {a.closed ? `Mês ${month} fechado por ${a.closing?.closed_by}` : `Mês ${month} aberto`}
        </span>
        {!a.closed && can(role, "gestor") && <Button size="sm" variant="outline" className="border-[#1E3A6E] bg-transparent" onClick={close} data-testid="biz-close-month-btn"><Lock className="mr-1 h-4 w-4" /> Fechar mês</Button>}
        {a.closed && can(role, "administrador") && <Button size="sm" variant="outline" className="border-[#1E3A6E] bg-transparent" onClick={reopen} data-testid="biz-reopen-month-btn"><Unlock className="mr-1 h-4 w-4" /> Reabrir mês</Button>}
        <Button size="sm" onClick={() => goTab("diagnostico")} data-testid="biz-overview-ai-btn">Analisar minha empresa com IA</Button>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <Kpi label="Faturamento" value={<V v={I.faturamento.receita_bruta} />} tone="green" hint={I.faturamento.meta ? `Meta ${brl(I.faturamento.meta)}` : "Sem meta definida"} testid="biz-kpi-revenue" />
        <Kpi label="Despesas no mês" value={<V v={H.has_data ? ['deducoes', 'custos_variaveis', 'despesas_fixas', 'despesas_financeiras', 'imposto_lucro'].reduce((s, k) => s + (H[k] || 0), 0) : null} />} tone="red"
          hint="Impostos (inclui o estimado pelo relatório contábil), custos, despesas fixas, juros e IR. Investimentos e empréstimos não entram" testid="biz-kpi-expenses" />
        <Kpi label="Lucro líquido" value={<V v={a.history.at(-1).has_data ? I.lucro_liquido.valor : null} />} tone={(I.lucro_liquido.valor || 0) >= 0 ? "green" : "red"} testid="biz-kpi-profit" />
        <Kpi label="EBITDA" value={<V v={a.history.at(-1).has_data ? I.ebitda.valor : null} />} tone="gold" testid="biz-kpi-ebitda" />
        <Kpi label="Caixa (saldo final)" value={<V v={I.fluxo_caixa.saldo_final} />} tone="blue" hint={`Projeção 90 dias: ${I.fluxo_caixa.projecao["90"] == null ? "—" : brl(I.fluxo_caixa.projecao["90"])}`} testid="biz-kpi-cash" />
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Section title="Últimos 12 meses (competência)" className="lg:col-span-2" testid="biz-overview-chart">
          <div className="h-72">
            <ResponsiveContainer>
              <ComposedChart data={a.history}>
                <CartesianGrid stroke="#1E3A6E" strokeDasharray="3 3" />
                <XAxis dataKey="month" stroke="#64748B" fontSize={11} /><YAxis stroke="#64748B" fontSize={11} tickFormatter={brlShort} width={70} />
                <Tooltip {...TT} /><Legend />
                <Bar dataKey="receita_bruta" name="Faturamento" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Line dataKey="lucro_liquido" name="Lucro líquido" stroke="#D4AF37" strokeWidth={2} />
                <Line dataKey="ebitda" name="EBITDA" stroke="#38BDF8" strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Section>
        <Section title="Alertas inteligentes" testid="biz-overview-alerts"><AlertList alerts={a.alerts} /></Section>
      </div>
      <TaxReport onSaved={reload} />
    </div>
  );
}
